// Common
import { Component, computed, DestroyRef, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { FullWindowService } from '@modules/common/services/full-window.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { Application } from '@modules/common/types/application';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { filter, map } from 'rxjs';
import {
  CollapsibleRailShellComponent,
  saveRailDocked,
} from '../collapsible-rail-shell/collapsible-rail-shell.component';

/** The list's `magneticSize` in the template: the narrowest it opens at. */
const SHEETS_LIST_MAGNETIC_SIZE = 300;
/** The list beside the open workbook keeps its own width and state, apart from the list beside the details. */
const SHEETS_EDITOR_LIST_KEY = 'sheetsEditorList';

/**
 * Sheets inside the app shell, mirroring NotesLayoutComponent.
 *
 * The client's shape (2026-09-25): Sheets is a section structured exactly like
 * Files — same sidebar, same list column, same detail view with the same tabs
 * and KP panel — with two differences. The detail view shows a PREVIEW of the
 * workbook, and a button EXPANDS the workbook across columns 2 and 3.
 *
 * Expansion is a child route rather than a boolean, so an open workbook is
 * linkable and the back button closes it. It also keeps the editor lazy: the
 * workbook chunk is fetched only when one is actually opened.
 *
 * SBUX-1145: Edit workbook and every new workbook made here open it full-window (`FullWindowService`): the rail
 * parks and the Dynamic Panel collapses as well, until the user reopens them or the workbook closes.
 */
@Component({
  selector: 'app-sheets-layout',
  templateUrl: './sheets-layout.component.html',
  styleUrls: ['./sheets-layout.component.less', '../../styles.less'],
  standalone: false,
})
export class SheetsLayoutComponent {
  public SidebarSplitViewKey = SidebarSplitViewKey;
  protected readonly app = Application.sheets;
  protected readonly fullWindow = inject(FullWindowService);
  protected readonly railFolded = computed(() => this.fullWindow.railFoldedApp() === this.app);

  private readonly splitView = inject(SplitViewService);
  private readonly railShell = viewChild(CollapsibleRailShellComponent);
  /**
   * SBUX-1145 (Wook, 2026-10-27: "Yes, add a list toggle"): beside an open workbook the list starts folded to its edge
   * tab, and once the user opens it, it stays open while workbooks or sheets change. A new full-window open folds it
   * again.
   */
  protected readonly editorListOpen = signal(false);
  private readonly editorListSaved = toSignal(this.splitView.getMinimized(SHEETS_EDITOR_LIST_KEY), {
    initialValue: false,
  });
  protected readonly editorListMinimized = computed(() => !this.editorListOpen() || this.editorListSaved());

  public constructor(private readonly router: Router) {
    // AM-113: opening Sheets is when an owner whose plan gained Sheets later gets the demo workbook. Asked in the
    // owner's own space, the one a demo belongs in (signup seeds personal spaces only; a team account has none),
    // and again on a switch back to it. sheets-app decides whether there is one to make.
    const spaces = inject(SpaceContextStore);
    const sheets = inject(SheetsService);
    effect(() => {
      if (spaces.type() === SpaceType.PERSONAL) untracked(() => sheets.requestDemo());
    });

    // A session starts only from a trigger's own navigation (`FullWindowStart`), while this page is on screen; it ends
    // when the workbook closes (the list's address, Back) or this page goes away. Switching workbooks or sheets
    // changes neither.
    inject(DestroyRef).onDestroy(this.fullWindow.attach(this.app));
    const openWorkbook = toSignal(
      router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.openWorkbookUrl()),
      ),
      { initialValue: this.openWorkbookUrl() },
    );
    let shown: string | null = null;
    effect(() => {
      const url = openWorkbook();
      untracked(() => {
        if (url !== null && shown === null) this.editorListOpen.set(false);
        if (url === null && shown !== null) this.endFullWindow();
        shown = url;
      });
    });
    effect(() => {
      this.fullWindow.starts();
      untracked(() => this.editorListOpen.set(false));
    });
    inject(DestroyRef).onDestroy(() => this.endFullWindow());
  }

  /** Works for both the guarded /sheets route and the unguarded /sheets-spike. */
  protected get expanded(): boolean {
    return this.router.url.split('?')[0].endsWith('/open');
  }

  private get base(): string {
    return this.router.url.startsWith('/sheets-spike') ? '/sheets-spike' : '/sheets';
  }

  protected open(): void {
    void this.router.navigate([`${this.base}/open`]);
  }

  protected close(): void {
    void this.router.navigate([this.base]);
  }

  /** The open workbook's address (its id and sheet), or null while the list shows. */
  private openWorkbookUrl(): string | null {
    return this.expanded ? this.router.url : null;
  }

  /**
   * A full-window session's end reopens the rail and the list and saves them open, whatever they were (Wook,
   * 2026-10-25); the Dynamic Panel is the shell layout's. A session-less workbook closes as it always has.
   */
  private endFullWindow(): void {
    if (!this.fullWindow.end(this.app)) return;
    const rail = this.railShell();
    if (rail) rail.dock();
    else saveRailDocked(this.app);
    this.splitView.reopen('sheetsList', SHEETS_LIST_MAGNETIC_SIZE);
  }
}
